import {
  BackWrapper,
  Container,
  ContainerWrapper,
  NoContentLabel,
  StickyTop,
  Title,
  TitleWrapper,
} from './SpecsPage.styled';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { SortDirection } from 'components/Table/types';
import BackButton from 'components/BackButton/BackButton';
import Table from 'components/Table/Table';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { VariableCard } from './VariableCard/VariableCard';
import { tableConfig } from './constants';
import {
  formatSpecVariables,
  IFormattedVariable,
  sortSpecVariables,
} from './helpers';
import { useNavigateBackFromConfiguratorSubPage } from 'utils/hooks/useNavigateBackFromConfiguratorSubPage';
import { useScrollToTopOnMount } from 'utils/hooks/useScrollToTopOnMount';
import { useElementHeight } from 'utils/hooks/useElementHeight';

const SpecsPage = () => {
  const { t } = useTranslation();

  const navigateBack = useNavigateBackFromConfiguratorSubPage();

  // Opened from the configurator, which keeps the window offset across the
  // navigation - this page is read from the top.
  useScrollToTopOnMount();

  const variables = useSelector(
    (state: IRootReducerState) => state.modelInfo.variables
  );

  const queryRequestStatus = useSelector(
    (state: IRootReducerState) => state.modelInfo.queryStatus
  );

  // Sorted here rather than by the backend, which returns the variables in
  // model order and has no sorting for them.
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(
    SortDirection.NONE
  );

  const formattedVariables: IFormattedVariable[] = sortSpecVariables(
    formatSpecVariables(variables),
    sortBy,
    sortDirection
  );

  const { isSmallTablet } = useBreakpointFlag();

  const stickyTopRef = useRef<HTMLDivElement>(null);
  const stickyTopHeight = useElementHeight(stickyTopRef);

  return (
    <ContainerWrapper>
      <StickyTop ref={stickyTopRef}>
        <BackWrapper>
          <BackButton
            destination={t('Configuration form')}
            onClick={navigateBack}
          />
        </BackWrapper>
        <TitleWrapper>
          <Title>{t('Specs')}</Title>
        </TitleWrapper>
      </StickyTop>
      <Container>
        {!queryRequestStatus?.attempt && !formattedVariables.length ? (
          <NoContentLabel>{t('There are currently no specs')}</NoContentLabel>
        ) : isSmallTablet ? (
          formattedVariables.map(
            (formattedVariable: IFormattedVariable, index: number) => (
              <VariableCard
                variable={formattedVariable}
                isLast={index === formattedVariables.length - 1}
                key={`variable-card-${formattedVariable.name}`}
              />
            )
          )
        ) : (
          <Table
            // Variables come with every model query, so they are usually
            // already here while a query runs - only show the skeleton when
            // there is nothing to show yet.
            isLoading={
              queryRequestStatus?.attempt && !formattedVariables.length
            }
            page={1}
            total={formattedVariables.length}
            hasExtraAction={false}
            loadPage={() => {
              //
            }}
            perPage={10}
            tableData={formattedVariables}
            tableConfig={tableConfig}
            sortParams={{ sortBy, setSortBy, sortDirection, setSortDirection }}
            withoutPagination={true}
            isStickyHeader={true}
            stickyHeaderOffset={stickyTopHeight}
            allowTextSelection={true}
          />
        )}
      </Container>
    </ContainerWrapper>
  );
};

export default SpecsPage;
